import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import type { InternalEventEmitter } from '@/common/hooks/useInternalEventEmitter';
import type { ChatConfig } from '@/common/types/chat.types';

const CHAT_SESSION_STARTED_KEY = "chatSessionStarted";

interface ChatContextType {
  chatSessionStarted: boolean;
  setChatSessionStarted: (value: boolean) => void;
  onGreetingAnimationComplete: () => void;
  internalEventEmitter: InternalEventEmitter;
  config: ChatConfig;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
  internalEventEmitter: InternalEventEmitter;
  config: ChatConfig;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children, internalEventEmitter, config }) => {
  const [chatSessionStarted, setChatSessionStarted] = useLocalStorageState(CHAT_SESSION_STARTED_KEY, { defaultValue: false });

  const onGreetingAnimationComplete = () => {
    setChatSessionStarted(true);
  };

  return (
    <ChatContext.Provider value={{ chatSessionStarted, setChatSessionStarted, onGreetingAnimationComplete, internalEventEmitter, config }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};
